/**
 * PRBoard - Personal Records leaderboard
 */

import { motion } from 'framer-motion';
import { Trophy, Medal, Award, Crown } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';

const PRBoard = ({ records }) => {
  // Muscle group colors
  const getMuscleColor = (group) => {
    const colors = {
      chest: 'text-red-400 bg-red-500/20',
      back: 'text-blue-400 bg-blue-500/20',
      shoulders: 'text-purple-400 bg-purple-500/20',
      arms: 'text-yellow-400 bg-yellow-500/20',
      legs: 'text-green-400 bg-green-500/20',
      core: 'text-orange-400 bg-orange-500/20',
      full_body: 'text-pink-400 bg-pink-500/20',
    };
    return colors[group] || 'text-gray-400 bg-gray-500/20';
  };

  // Muscle group labels
  const muscleLabels = {
    chest: 'Göğüs',
    back: 'Sırt',
    shoulders: 'Omuz',
    arms: 'Kol',
    legs: 'Bacak',
    core: 'Karın',
    full_body: 'Tüm Vücut',
  };

  // Rank icons
  const getRankIcon = (rank) => {
    if (rank === 0) return <Crown className="w-5 h-5 text-yellow-400" />;
    if (rank === 1) return <Medal className="w-5 h-5 text-gray-300" />;
    if (rank === 2) return <Award className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 h-5 flex items-center justify-center text-sm text-gray-500">{rank + 1}</span>;
  };

  if (!records?.length) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-gray-900 border border-gray-800 rounded-xl p-6 text-center"
      >
        <Trophy className="w-12 h-12 text-gray-700 mx-auto mb-3" />
        <p className="text-gray-400">Henüz kişisel rekor yok</p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center gap-2 p-4 border-b border-gray-800">
        <Trophy className="w-5 h-5 text-yellow-500" />
        <h3 className="font-semibold text-white">Kişisel Rekorlar</h3>
        <span className="text-xs text-gray-500 ml-auto">En yüksek ağırlıklar</span>
      </div>

      {/* Records List */}
      <div className="divide-y divide-gray-800">
        {records.map((record, idx) => (
          <motion.div
            key={record.exercise}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 + idx * 0.05 }}
            className={`flex items-center gap-4 p-4 hover:bg-gray-800/50 transition-colors ${
              idx === 0 ? 'bg-gradient-to-r from-yellow-500/10 to-transparent' : ''
            }`}
          >
            {/* Rank */}
            <div className="w-8 flex-shrink-0">
              {getRankIcon(idx)}
            </div>

            {/* Exercise Info */}
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{record.exercise}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs text-gray-500">
                  {format(parseISO(record.date), 'd MMM yyyy', { locale: tr })}
                </span>
                {record.muscle_group && (
                  <span className={`text-xs px-1.5 py-0.5 rounded ${getMuscleColor(record.muscle_group)}`}>
                    {muscleLabels[record.muscle_group] || record.muscle_group}
                  </span>
                )}
              </div>
            </div>

            {/* Weight & Reps */}
            <div className="text-right flex-shrink-0">
              <p className="text-xl font-bold text-orange-400">
                {record.weight} <span className="text-sm text-gray-400">kg</span>
              </p>
              {record.reps && (
                <p className="text-xs text-gray-500">{record.reps} tekrar</p>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Footer note */}
      {records.length >= 10 && (
        <div className="px-4 py-3 bg-gray-800/30 text-center">
          <span className="text-xs text-gray-500">En iyi 10 rekor gösteriliyor</span>
        </div>
      )}
    </motion.div>
  );
};

export default PRBoard;
